import {
  Mail,
  Newspaper,
  Gift,
  Megaphone,
  Receipt,
  CalendarDays,
  Heart,
  Star,
  FileText,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  useEmailTemplates,
  type EmailTemplateRecord,
} from "@/hooks/use-email-templates";

export interface EmailTemplate extends EmailTemplateRecord {
  icon: LucideIcon;
}

interface TemplateLibraryProps {
  onSelect: (template: EmailTemplate) => void;
  onClose: () => void;
}

function iconForCategory(category: string): LucideIcon {
  switch (category.toLowerCase()) {
    case "onboarding":
      return Mail;
    case "regular":
      return Newspaper;
    case "marketing":
      return Gift;
    case "updates":
      return Megaphone;
    case "transactional":
      return Receipt;
    case "events":
      return CalendarDays;
    case "retention":
      return Heart;
    case "feedback":
      return Star;
    default:
      return FileText;
  }
}

function mapTemplate(template: EmailTemplateRecord): EmailTemplate {
  return {
    ...template,
    icon: iconForCategory(template.category),
  };
}

export function TemplateLibrary({ onSelect, onClose }: TemplateLibraryProps) {
  const { templates, isLoading, error } = useEmailTemplates();
  const mappedTemplates = templates.map(mapTemplate);
  const categories = [...new Set(mappedTemplates.map((template) => template.category))];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Template Library</h2>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Browse system starters and saved team templates backed by the mounted email template API.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
          >
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {isLoading ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)]/60 px-6 py-10 text-center text-sm text-[var(--color-muted-foreground)]">
              Loading mounted templates…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-6 py-4 text-sm text-[var(--color-error)]">
              {error}
            </div>
          ) : mappedTemplates.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)]/60 px-6 py-10 text-center">
              <p className="text-sm font-medium text-[var(--color-foreground)]">No templates available yet</p>
              <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                Save a reusable template from the email builder or campaign wizard to populate this library.
              </p>
            </div>
          ) : (
            categories.map((category) => (
              <div key={category} className="mb-6 last:mb-0">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  {category}
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {mappedTemplates
                    .filter((template) => template.category === category)
                    .map((template) => {
                      const Icon = template.icon;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => onSelect(template)}
                          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-left transition-all hover:border-[var(--color-primary)]/40 hover:shadow-md"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-surface)]">
                              <Icon className="h-4.5 w-4.5 text-[var(--color-primary)]" />
                            </div>
                            <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                              {template.source === "system" ? "System" : "Saved"}
                            </span>
                          </div>
                          <p className="mt-3 text-sm font-medium text-[var(--color-foreground)]">{template.name}</p>
                          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{template.description}</p>
                          <p className="mt-3 line-clamp-2 text-xs text-[var(--color-foreground)]/80">{template.subject}</p>
                        </button>
                      );
                    })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
